import { Logger } from "@suse-o11y/logger";
import { onTimeout } from "@suse-o11y/on-timeout";
import React from "react";

import { SystemScreen } from "~/components/system-screen";
import { isExpiredSubscription, isSubscriptionError, isUnlicensedSubscription } from "~/models/dto";

import { type LicenseError, useUpdateLicenseKey } from "../../../queries";
import { LoadingSpinner } from "../loading-spinner/LoadingSpinner";

import { LicenseEntry } from "./LicenseEntry";
import { LicenseSuccess } from "./LicenseSuccess";

const MINIMUM_SPINNER_TIME_MILLISECONDS = 500;

export interface LicenseModalContainerProps {
	daysLeft: undefined | number;
	onDismiss: VoidFunction;
	error: LicenseError;
}

export const LicenseWarning: React.FC<LicenseModalContainerProps> = ({ daysLeft, onDismiss, error: licenseError }) => {
	// a loading state for the minimum time the spinner should be shown
	const [isMinimumLoading, setIsMinimumLoading] = React.useState(false);
	const [error, setError] = React.useState(licenseError);

	const {
		mutate: updateLicenseKey,
		data,
		isLoading,
	} = useUpdateLicenseKey({
		onMutate: () => {
			setIsMinimumLoading(true);
			setError(undefined);
		},
		onError: (httpError) => {
			const firstApiError = httpError.apiErrors?.[0];

			if (!isSubscriptionError(firstApiError)) {
				Logger.namespace("LicenseKeyUpdate").error("Unexpected response from server.", httpError);
				setError("failure");
			} else if (isUnlicensedSubscription(firstApiError.subscriptionState)) {
				setError(firstApiError.subscriptionState.reason);
			} else if (isExpiredSubscription(firstApiError.subscriptionState)) {
				setError("expired");
			}
		},
	});

	React.useEffect(() => {
		if (isMinimumLoading) {
			return onTimeout(() => {
				setIsMinimumLoading(false);
			}, MINIMUM_SPINNER_TIME_MILLISECONDS);
		}
	}, [isMinimumLoading]);

	if (isMinimumLoading || isLoading) {
		return (
			<SystemScreen className="min-h-full">
				{/* set a minimum height to prevent logo jumping when transitioning between the form and loading state */}
				<div className="min-h-[194px] content-center">
					<LoadingSpinner size="large" />
					<div className="mt-5">Registering...</div>
				</div>
			</SystemScreen>
		);
	}

	if (data != null && error == null) {
		return <LicenseSuccess />;
	}

	return <LicenseEntry daysLeft={daysLeft} error={error} onClose={onDismiss} onRegister={updateLicenseKey} />;
};
